import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, Observable, of, Subject, Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';

import { DataService } from '../data/data.service';

import * as mapboxgl from 'mapbox-gl';
import * as geojsonArea from '@mapbox/geojson-area';
import * as MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import CircleMode from 'src/app/helpers/mapBoxCircleMode';
import RectMode from 'src/app/helpers/mapBoxRectMode';
import { MapBoxGrid } from 'src/app/helpers/mapBoxGrid';
import { AlternateDrawStyle } from 'src/app/helpers/mapBoxDrawStyles';
import { Deck } from '@deck.gl/core';
import { Tile3DLayer } from '@deck.gl/geo-layers';
import { CesiumIonLoader } from '@loaders.gl/3d-tiles';
import RotateMode from 'mapbox-gl-draw-rotate-mode';
import noUiSlider from 'nouislider';
import { centroid } from '@turf/turf';
import { AnnotationService } from '../annotation/annotation.service';

import * as turf from '@turf/turf';
import { ConvertDMSToDD, GeoCluster } from 'src/app/helpers/geoCluster';
import { SplitViewService } from '../split-view/split-view.service';
import { LocationStoreService } from 'src/app/stores/location-store/location-store.service';
import { GenericTypeStoreService } from 'src/app/stores/generic-types-store/generic-type-store.service';
import { GalleryImagesStoreService } from 'src/app/stores/gallery-images-store/gallery-images-store.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { AnnotationStoreService } from 'src/app/stores/annotation-store/annotation-store.service';
import { OpenlayerService } from '../openlayer/openlayer.service';
import { NgxSpinnerService } from 'ngx-spinner';

declare var $;
declare var THREE;
declare var proj4;
declare let html2canvas: any;

@Injectable({
  providedIn: 'root',
})
export class MapService implements OnDestroy {
  private mapMarkers = [];
  markerDataGalleryImage;
  markerSearchedCoordinate;
  prevMarkerDataGalleryImage = '';
  // Layer Tools
  isOrtho$ = new BehaviorSubject(false);
  isDSM$ = new BehaviorSubject(false);
  isThermal$ = new BehaviorSubject(false);
  isThreeD$ = new BehaviorSubject(false);
  isThreeDMesh$ = new BehaviorSubject(false);
  isGalleryImages$ = new BehaviorSubject(false);
  isGrid$ = new BehaviorSubject(true);
  isAnnotationMarker$ = new BehaviorSubject(false);
  isDraw$ = new BehaviorSubject(null);
  hasDrawing$ = new BehaviorSubject(false);

  activeAnnotation$ = new BehaviorSubject(null);
  active3dAnnotation$ = new BehaviorSubject([]);
  activeGrid$ = new BehaviorSubject(null);

  // Helper
  isMapLoaded$ = new BehaviorSubject(false);
  isDeckLoaded$ = new BehaviorSubject(false);
  isShowLocation$ = new BehaviorSubject(false);
  currZoom$ = new Subject<any>();

  annotationAreaSizeWithGrid$: BehaviorSubject<number> = new BehaviorSubject(0);
  annotationCentroidWithGrid$: BehaviorSubject<any> = new BehaviorSubject(null);

  markerEvents$: Subject<IMarkerEvents> = new Subject<IMarkerEvents>();

  // radiusMode = MapboxDraw.modes.draw_line_string;

  geocoder;
  prevAnnot;
  draw;

  drawField: any = {
    size: '',
    coordinates: '',
  };

  map: mapboxgl.Map; // this.Map refer to current root Map that is being used
  deck: Deck; // deck gl
  deckIsActive: boolean = true;

  INITIAL_VIEW_STATE = {
    latitude: 0,
    longitude: 0,
    zoom: 4,
    bearing: 0,
    pitch: 60,
  };

  activeGrid = []; // list of rendered grid
  listAnnotation = []; // list of created annotation
  annotationSubs: Subscription;
  annotationServiceFindSub: Subscription;
  galleryImagesSub: Subscription;

  isSpotlightMode$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  // threeFont: typeof THREE.Font;
  threeFont: any;

  isGrid: boolean = false;
  prevCentroid;

  isGridEditMode$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  isShiftKeyDown$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  drawBuilt$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  threeDScreenshotBlob$: BehaviorSubject<any> = new BehaviorSubject(null);
  clusteredCoors: any[];

  $topMostOrthoMapLayer = new BehaviorSubject({});

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private dataService: DataService,
    private zone: NgZone,
    private splitViewService: SplitViewService,
    private annotationService: AnnotationService,
    private locationStore: LocationStoreService,
    private genericTypeStore: GenericTypeStoreService,
    private galleryImagesStore: GalleryImagesStoreService,
    private annotationStore: AnnotationStoreService,
    private toastr: ToastrService,
    private _t: TranslateService,
    private openlayerService: OpenlayerService,
    private spinner: NgxSpinnerService
  ) {
    mapboxgl.accessToken = environment.mapbox.accessToken;
  }

  set currentMap(map) {
    this.map = map;
  }

  buildMap(metadata, rootMap = false): mapboxgl.Map {
    const map = new mapboxgl.Map(metadata);
    this.INITIAL_VIEW_STATE = metadata;

    if (rootMap) this.currentMap = map;
    return map;
  }

  // this is called from split view component once the base map is ready
  setBaseMap(baseMap: mapboxgl.Map, baseMapState: any) {
    this.INITIAL_VIEW_STATE = baseMapState;
    this.map = baseMap;
    this.currentMap = baseMap;

    setTimeout(() => {
      this.rotateNeedsPause = false;
    }, 10000);

    // sets onTick to run outside of angular, for performance
    this.zone.runOutsideAngular(() => this.onTick());

    const onMapDragStop = (e) => {
      this.rotateNeedsPause = false;
    };

    const onMapDragStart = (e) => {
      this.rotateNeedsPause = true;
      this.map.once('dragend', onMapDragStop);
    };

    const onMapDrag = (e) => {
      this.rotateNeedsPause = true;
      this.map.once('dragend', onMapDragStop);
    };

    this.map.on('drag', onMapDrag);

    this.activePotreeJSONData$.subscribe((data) => {
      if (!data) {
        return;
      }

      //this.mapProjection = "WGS84";
      this.mapProjection = '+proj=longlat +ellps=WGS84 +datum=WGS84 +no_defs';
      this.potreeProjection = data.projection;
    });
  }

  buildDraw(severities?, map = this.map): void {
    const __draw = (this.draw = new MapboxDraw({
      displayControlsDefault: false,
      modes: Object.assign({
        ...MapboxDraw.modes,
        draw_circle: CircleMode,
        draw_rect: RectMode,
        rotate: RotateMode,
      }),
      styles: AlternateDrawStyle(severities),
      userProperties: true,
    }));

    this.drawBuilt$.next(true);

    //added scale but not function
    var scale = new mapboxgl.ScaleControl({
      maxWidth: 80,
      unit: 'imperial',
    });
    map.addControl(scale, 'bottom-right');

    //added scale but not function
    var scale2 = new mapboxgl.ScaleControl({
      maxWidth: 80,
      unit: 'matric',
    });
    map.addControl(scale2, 'bottom-right');

    const self = this;
    map.addControl(__draw);

    map.on('draw.create', (e) => this.updateArea(e, self, map));
    map.on('draw.delete', (e) => this.updateArea(e, self, map));
    map.on('draw.update', (e) => this.updateArea(e, self, map));
    map.on('draw.keyup.escape', (e) => {
      this.isDraw$.next(null);
      this.changeDrawMode('simple_select');
      this.deleteSelectedAnnot(e.feature.id);
    });

    const onMapDrawSelectionChange = (e) => {
      if (e.features.length > 0)
        this.prevAnnot = {
          id: e.features[0].id,
          size: this.calculateAreaInMeterSq(e.features[0]),
        };
      const s = __draw.getSelected();

      if (e.features.length != 0 && this.lastActiveDrawId != e.features[0].id) {
        this.addGridToDraw(__draw.getSelected().features[0], map, true);
      }
    };

    map.on('draw.selectionchange', onMapDrawSelectionChange);

    return __draw;
  }

  buildDeck(): void {
    this.deck = new Deck({
      canvas: 'deck-canvas',
      width: '100%',
      height: '100%',
      initialViewState: this.INITIAL_VIEW_STATE,
      controller: true,
      onViewStateChange: ({ viewState }) => {
        // this.map.jumpTo({
        //   center: [viewState.longitude, viewState.latitude],
        //   zoom: viewState.zoom,
        //   bearing: viewState.bearing,
        //   pitch: viewState.pitch,
        // });
      },
      onClick: (e) => {
        //this.map.fire('click', { lngLat: e.lngLat, point: new mapboxgl.Point(e.x, e.y) });
        var features = this.map.queryRenderedFeatures(
          new mapboxgl.Point(e.x, e.y)
        );
      },
      layers: [],
    });
    this.deckIsActive = false;
  }

  allDrawings: any[] = [];

  updateArea(event, self, map = this.map): void {
    const { type, features } = event;
    let size;

    if (features[0].geometry) {
      const geometry = features[0].geometry;
      size = self.convertToHa(self.calculateArea(geometry));
      self.currentField = {
        size: `${size} Ha`,
        coordinates: geometry.coordinates,
      };
    } else {
      size = 0;
      self.currentField = {
        size: `${size} Ha`,
        coordinates: [],
      };
    }

    let existingDrawing = this.allDrawings.find(
      (drawing) => drawing.id == features[0].id
    );
    if (existingDrawing) {
      this.allDrawings[this.allDrawings.indexOf(existingDrawing)] = features[0];
    } else {
      this.allDrawings.push(features[0]);
    }
    this.updateActiveAnnotation();
    if (this.allDrawings.length > 0) this.hasDrawing$.next(true);

    if (type == 'draw.create') {
      this.lastActiveDrawId = features[0].id;

      if (features[0].geometry.type !== 'LineString') {
        setTimeout(() => {
          this.changeDrawMode('simple_select', {
            featureIds: [this.lastActiveDrawId],
          });
        }, 10);
        this.gridRotateAngle = map.getBearing();

        this.addGridToDraw(features[0], map, true);
      } else {
        this.onLineStringCreated(features[0].geometry.coordinates, map);
        this.isDraw$.next(null);
      }
    }

    if (type == 'draw.update') {
      if (features[0].geometry.type !== 'LineString') {
        if (
          this.prevAnnot?.id === features[0].id &&
          this.prevAnnot.size.toFixed(3) !==
            this.calculateAreaInMeterSq(features[0]).toFixed(3)
        ) {
          this.gridSelectedIds = [];
          this.updateGridsData(this.prevAnnot.id, {
            rotateAngle: this.gridRotateAngle,
            selectedIds: this.gridSelectedIds,
          });
        }

        this.addGridToDraw(features[0], map, true);
      } else {
        this.onLineStringCreated(features[0].geometry.coordinates, map);
      }
    }

    if (type == 'draw.delete') {
      this.onDeleteDrawingItem(event.features[0].id);
      if (features[0].geometry.type !== 'LineString') {
        const newAreaSize = this.calculateGridAreaSize();
        this.annotationAreaSizeWithGrid$.next(newAreaSize);
      } else {
        this.removeAllLineStringMarkers();
      }
    }
  }

  lineStringMarkers = [];

  onLineStringCreated(points, map = this.map) {
    let totalLength = 0;
    this.removeAllLineStringMarkers();
    points.forEach((point, index) => {
      if (index > 0 && index != points.length) {
        const from = turf.point(points[index - 1]);
        const to = turf.point(points[index]);

        const lineDist = turf.distance(from, to);
        const lineDistInM = lineDist * 1000;
        totalLength += lineDistInM;

        const midpoint = turf.midpoint(from, to);

        var el = document.createElement('div');
        el.className = 'marker';
        el.style.width = 80 + 'px';
        el.style.height = 5 + 'px';

        var span = document.createElement('span');
        span.className = 'span';
        span.appendChild(
          document.createTextNode(lineDistInM.toFixed(3) + ' m')
        );

        el.appendChild(span);

        // add marker to map
        const labelMarker = new mapboxgl.Marker(el)
          .setLngLat(midpoint.geometry.coordinates)
          .addTo(map);

        this.lineStringMarkers.push(labelMarker);
      }
    });
    this.annotationAreaSizeWithGrid$.next(parseFloat(totalLength.toFixed(3)));
  }

  removeAllLineStringMarkers() {
    this.lineStringMarkers.forEach((marker) => {
      marker.remove();
    });
  }

  onDeleteDrawingItem(drawId) {
    this.removeAllGrid();
    const deletedDrawing = this.allDrawings.find(
      (drawing) => drawing.id == drawId
    );
    this.allDrawings.splice(this.allDrawings.indexOf(deletedDrawing), 1);
    const deletedGridData = this.allGridsData.find(
      (grid) => grid.drawId == drawId
    );
    this.allGridsData.splice(this.allGridsData.indexOf(deletedGridData), 1);
  }

  updateActiveAnnotation() {
    if (this.isThreeD$.getValue()) {
      return;
    }
    const collect = {
      drawing: this.allDrawings,
      grids: this.allGridsData,
    };

    this.activeAnnotation$.next(collect);
  }

  updateMeasurements3D(measurements) {
    this.active3dAnnotation$.next(measurements);
    const measurementsClone = Array.from(measurements);

    setTimeout(() => {
      this.getMapScreenshot3D();
      this.calculateArea3D(measurementsClone);
    }, 500);
  }

  calculateArea3D(measurements) {
    const areaMeasures = measurements.filter(
      (measure) => measure.type == 'Area'
    );
    let totalSize = 0;
    areaMeasures.forEach((area) => {
      const areaSize = this.getAreaByLineDistance(area.edgeLabelsDistance);
      totalSize += areaSize;
    });
    this.annotationAreaSizeWithGrid$.next(totalSize);
  }

  mapProjection: any;
  potreeProjection: any;
  activePotreeJSONData$: BehaviorSubject<any> = new BehaviorSubject(null);

  convert3DPointsToLngLats(points): any {
    const cloned = Array.from(points);
    let lngLats = [];
    cloned.forEach((point) => {
      const convert = proj4(
        this.potreeProjection,
        this.mapProjection,
        point['position']
      );
      lngLats.push([convert.x, convert.y]);
    });

    if (lngLats[lngLats.length - 1] !== lngLats[0]) {
      lngLats.push(lngLats[0]);
    }
    return lngLats;
  }

  getArea(points) {
    let area = 0;
    let j = points.length - 1;

    for (let i = 0; i < points.length; i++) {
      let p1 = points[i].position;
      let p2 = points[j].position;
      area += (p2.x + p1.x) * (p1.y - p2.y);
      j = i;
    }

    const a = Math.abs(area / 2);
    const areaSize = (a / Math.pow(1, 2)) * Math.pow(1, 2); //convert to square meters then to the square display unit

    return parseFloat(areaSize.toFixed(3));
  }

  getAreaByLineDistance(lineDistances = []) {
    let area = 0;

    if (lineDistances.length >= 3) {
      if (lineDistances.length >= 5) {
        let totalDist = 0;
        lineDistances.forEach((dist, index) => {
          if (index < 4) {
            totalDist += dist;
          }
        });
        const sideDist = totalDist / 4;
        area = sideDist * sideDist;
        //area = this.edgeLabelsDistance[0] * this.edgeLabelsDistance[1];
      } else {
        const sides = Array.from(lineDistances).slice(0, 3);

        let sidesTotal = 0;
        sides.forEach((side) => {
          sidesTotal += side;
        });

        sidesTotal = sidesTotal / 2;

        let allMinus = [];
        sides.forEach((side) => {
          allMinus.push(sidesTotal - side);
        });

        let s = allMinus[0];
        allMinus.forEach((minus, index) => {
          if (index > 0) {
            s *= minus;
          }
        });

        area = Math.sqrt(sidesTotal * s);
      }
    }

    return area;
  }

  set currentField(data) {
    this.drawField = data;
  }

  get currentField() {
    return this.drawField;
  }

  public calculateArea(geoJson): void {
    return geojsonArea.geometry(geoJson);
  }

  public calculateCentroid(geometry) {
    return centroid(geometry).geometry.coordinates;
  }

  public convertToHa(meterSquare): number {
    return +(meterSquare / 10000).toFixed(3);
  }

  /**
   * Enables map click and allows the caller to disable on demand
   * Returns an observable and a function to disable the map by the caller
   * The disableFn ensures that no multiple event listener is created
   */
  enableMapCLick(): { click$: Observable<any>; disableFn: () => void } {
    const sub: Subject<any> = new Subject();

    const res = (clickEvt) => {
      sub.next(clickEvt);
    };
    this.map.getCanvas().style.cursor = 'crosshair';
    this.map.on('click', res);

    return {
      click$: sub,
      disableFn: () => {
        this.map.getCanvas().style.cursor = '';
        this.map.off('click', res);
      },
    };
  }

  buildSearch(): void {
    // this.geocoder = new MapboxGeocoder({ accessToken: mapboxgl.accessToken });
    this.geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      mapboxgl,
    });

    // this.geocoder.addTo('#geocoder-container');

    document
      .getElementById('geocoder')
      .appendChild(this.geocoder.onAdd(this.map));
  }

  makeMarker(mark, index, map = this.map) {
    if (!map) {
      return;
    } // map not ready

    const el = document.createElement('div');

    el.className = 'point';
    if (mark.properties.status === 'inactive') el.classList.add('inactive');
    el.setAttribute('marker_id', mark.properties.id);

    var displayName =
      '<div class="box-name a">' + mark.properties.title + '</div>';
    el.innerHTML = displayName;

    el.addEventListener('mouseenter', (event) => {
      this.markerEvents$.next({
        onMarkerEnter: {
          markerTitle: mark.properties.title,
          markerId: mark.properties.id,
          mouseEvent: event,
        },
      });

      if (this.dataService.sticky$.value.length === 0)
        this.dataService.sticky$.next(['project']);

      document
        .querySelectorAll('.project-card')
        .forEach((elem) => elem.classList.remove('show'));

      const projectCard = document.getElementById(
        `project-card-${mark.properties.id}`
      );
      const projectCardContainer = document.querySelector(
        '.dropup:nth-child(2) .dropup-content .list-group'
      );

      if (projectCard && projectCardContainer) {
        const projectCardOffset = projectCard.offsetTop;
        projectCardContainer.scrollTo({
          top: projectCardOffset,
          behavior: 'smooth',
        });

        projectCard.classList.add('show');
      }
    });

    el.addEventListener('mouseleave', (event) => {
      this.markerEvents$.next({
        onMarkerLeave: { markerId: mark.properties.id, mouseEvent: event },
      });
      // this.dataService.sticky$.next([]);
      // projectCard.classList.remove('show');
    });
    try {
      const m = new mapboxgl.Marker(el, { offset: [0, -25] })
        .setLngLat(mark.geometry.coordinates)
        .addTo(map);
      this.mapMarkers.push(m);

      this.clickMarker(mark, el, 'details');
    } catch (error) {}
  }

  makeMarkers(location, map = this.map, defaultZoom?) {
    if (!map) {
      return;
    }

    this.removeAllMarker().then((r) => {
      // if location has childrens or location doesnt have any map layer
      if (!location) return;
      if (
        location.features.length > 1 ||
        !location?.features[0]?.properties?.map_layer?.length
      ) {
        location.features.forEach((mark, index) => {
          this.makeMarker(mark, index, map);
        });
        this.rotateNeedsPause = true;
        /**
         * having issue where cannot zoom from home to location, but can from location to asset.
         * also will suddenly zoom in when nav from asset to home, right after refreshing the asset page.
         */
        // console.log('DEFAULTZOOM', defaultZoom)
        if (defaultZoom !== undefined && location.features.length === 0) {
          this.zoomToZoomLevel(defaultZoom);
        } else {
          map.once('moveend', (e) => (this.rotateNeedsPause = false));
          this.fitboundCoordinates(
            location.features.reduce(
              (acc, i) => [...acc, i.geometry.coordinates],
              []
            ),
            {
              padding: 300,
              maxZoom: defaultZoom !== undefined ? defaultZoom : 19,
              pitch: 60,
            }
          );
        }
      } else {
        // if only one marker, then fly to the location with a limited zoom
        this.rotateNeedsPause = true;
        map.once('moveend', (e) => (this.rotateNeedsPause = false));

        map.flyTo({
          center: location.features[0].geometry.coordinates,
          zoom: defaultZoom !== undefined ? defaultZoom : 19,
          pitch: 0,
          speed: 5,
          essential: true,
          duration: 1000,
        });
      }
    });
  }

  getLocation(data) {
    const features = data.map((el) => {
      return {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: el.coordinate,
        },
        properties: {
          id: el.id,
          status: el.status,
          title: el.name,
          map_layer: el.map_layer,
          description: el.description,
        },
      };
    });

    return of({ type: 'FeatureCollection', features });
  }

  addOrtho(ortho, id, map = this.map) {
    if (!map) {
      return;
    } // map not ready
    if (map.getLayer(`${ortho.layer_type}-${id}-layer`)) return;
    if (map.getSource(`${ortho.layer_type}-${id}`)) return;
    const source = {
      type: 'raster',
      tiles: [
        ortho.external_url.replace(/(?=\w+\.\w{3,4}$).+/, '{z}/{x}/{y}.png'),
      ],
      tileSize: 256,
      maxzoom: 24,
    };
    const layer = {
      id: `${ortho.layer_type}-${id}-layer`,
      type: 'raster',
      source: `${ortho.layer_type}-${id}`,
      maxzoom: 24,
    };
    this.isMapLoaded$.subscribe((res) => {
      if (res) {
        map.addSource(
          id ? `${ortho.layer_type}-${id}` : 'mapSyncOrtho',
          source
        );
        map.addLayer(layer, 'contour-label'); // contour-label is the lowest label layer inside mapbox
        this.isOrtho$.next(true);
      }
    });
    this.$topMostOrthoMapLayer.next(ortho);
  }

  addThreeDMesh(threeDMeshUrl, id, map = this.map) {
    if (!map) {
      return;
    } // map not ready
    this.isThreeDMesh$.next(true);
  }

  toggleSatellite(isSatellite) {
    if (isSatellite) {
      const source = {
        type: 'raster',
        tiles: [
          'https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v9/tiles/{z}/{x}/{y}?access_token=' +
            environment.mapbox.accessToken,
        ],
        tileSize: 256,
      };
      const layer = {
        id: 'raster-satellite-streets',
        type: 'raster',
        source: 'raster-satellite-streets-source',
      };
      this.map.addSource('raster-satellite-streets-source', source);
      this.map.addLayer(layer, 'admin-0-boundary-disputed');
    } else {
      this.map.removeLayer('raster-satellite-streets');
      this.map.removeSource('raster-satellite-streets-source');
    }
  }

  removeOrtho(id?, map = this.map) {
    //
    this.isMapLoaded$.subscribe((res) => {
      if (res) {
        if (map.getLayer(`ortho-${id}-layer`))
          map.removeLayer(`ortho-${id}-layer`);
        if (map.getSource(`ortho-${id}`)) map.removeSource(`ortho-${id}`);

        this.isOrtho$.next(false);
      }
    });
  }

  removeDSM(id?, map = this.map) {
    this.isMapLoaded$.subscribe((res) => {
      if (res) {
        if (map.getLayer('dsm-layer')) map.removeLayer('dsm-layer');
        if (map.getSource(`dsm-${id}`)) map.removeSource(`dsm-${id}`);
        this.isDSM$.next(false);
      }
    });
  }

  removeThermal(id?, map = this.map) {
    this.isMapLoaded$.subscribe((res) => {
      if (res) {
        if (map.getLayer('thermal-layer')) map.removeLayer('thermal-layer');
        if (map.getSource(`thermal-${id}`)) map.removeSource(`thermal-${id}`);
        this.isThermal$.next(false);
      }
    });
  }

  removeThreeDMesh(id?, map = this.map) {
    this.isThreeDMesh$.next(false);
  }

  removeAllOrtho(map = this.map) {
    this.isMapLoaded$.subscribe((res) => {
      if (res) {
        const allOrthoLayer = map
          .getStyle()
          .layers.filter((s) => s.id.includes('ortho'));
        allOrthoLayer.forEach((l) => {
          map.removeLayer(l.id);
          map.removeSource(l.source);
        });

        this.isOrtho$.next(false);
      }
    });
  }

  removeAllDSM(map = this.map) {
    this.isMapLoaded$.subscribe((res) => {
      if (res) {
        const allDSMLayer = map
          .getStyle()
          .layers.filter((s) => s.id.includes('dsm'));
        allDSMLayer.forEach((l) => {
          map.removeLayer(l.id);
          map.removeSource(l.source);
        });
        this.isDSM$.next(false);
      }
    });
  }

  removeAllThermal(map = this.map) {
    this.isMapLoaded$.subscribe((res) => {
      if (res) {
        const allThermalLayer = map
          .getStyle()
          .layers.filter((s) => s.id.includes('thermal'));
        allThermalLayer.forEach((l) => {
          map.removeLayer(l.id);
          map.removeSource(l.source);
        });
        this.isThermal$.next(false);
      }
    });
  }

  removeAllGrid(theMap?) {
    this.isMapLoaded$.subscribe((res) => {
      if (res) {
        const map = theMap || this.map;
        const allGridLayer = map
          .getStyle()
          .layers.filter((l) => l.id.includes('grid'));
        allGridLayer.forEach((l) => {
          map.off('click', l.id, this.onMapClickForGrid);
          map.off('mousemove', l.id, this.onMapMouseMoveForGrid);
          map.removeLayer(l.id);
          map.removeSource(l.source);
        });
        $('.slider-grid').attr('disabled', true);
        $('.rotateInput').attr('disabled', true);
        $('.reset-grid-button').attr('disabled', true);
      }
    });
  }

  gridRotateAngle = 0;
  gridRotateTestIntervalId;
  gridSelectedIds = [];
  allGridsData: any[] = [];
  lastActiveDrawId: string;
  lastActiveDrawing = {};
  rotateSlider;

  addGridToDraw(
    draw,
    targetMap: mapboxgl.Map = this.map,
    editMode: boolean = false,
    fromGridToggle: boolean = false
  ): void {
    if (draw.geometry.type === 'LineString') {
      return;
    }
    if (draw) {
      this.lastActiveDrawing[targetMap._mapId] = draw;

      const allAnnotations = this.annotationStore.annotations$.value;
      const thisAnnotation = allAnnotations.find(
        (annotation) => annotation.id === draw.properties?.annotation_id
      );
      if (thisAnnotation) {
        const data = JSON.parse(thisAnnotation.area);
        this.allDrawings = data.drawing;
        if (data.grids) {
          this.allGridsData = data.grids;
        } else {
          this.allGridsData = [];
        }
      }
    }
    if (!this.allGridsData) return;
    let drawID;

    if (this.lastActiveDrawing[targetMap._mapId]?.id) {
      drawID = this.lastActiveDrawing[targetMap._mapId].id;
    } else {
      drawID = this.lastActiveDrawing[targetMap._mapId].properties.drawId;
    }

    // halt redraw if same drawing and not edit mode to prevent flicker on click
    // if (drawID == this.lastActiveDrawId && (!editMode && !fromGridToggle)) {

    const gridData = this.updateGridsData(drawID, null);

    this.gridRotateAngle = gridData.rotateAngle;
    this.gridSelectedIds = gridData.selectedIds;

    this.splitViewService.lastActiveDrawIds[targetMap._mapId] = drawID;

    if (!this.isGrid$.getValue()) {
      return;
    }

    const layerID = `layer-${drawID}-grid`;
    const sourceID = `source-${drawID}-grid`;

    if (targetMap.getLayer(layerID)) {
      targetMap.off('click', layerID, this.onMapClickForGrid);
      targetMap.off('mousemove', layerID, this.onMapMouseMoveForGrid);
      targetMap.removeLayer(layerID);
    }

    if (targetMap.getSource(sourceID)) {
      targetMap.removeSource(sourceID);
    }

    this.removeAllGrid(targetMap);

    const grids: any = new MapBoxGrid(
      this.lastActiveDrawing[targetMap._mapId],
      gridData
    );

    targetMap.addSource(sourceID, {
      type: 'geojson',
      data: grids,
    });

    targetMap.addLayer({
      id: layerID,
      type: 'fill',
      source: sourceID,
      paint: {
        'fill-color': [
          'case',
          ['==', ['get', 'selected'], true],
          'rgba(255, 255, 255, 0.4)', // white transparent
          'transparent',
        ],
        'fill-outline-color': [
          'case',
          ['>=', ['get', 'id'], 0],
          '#7FFF00', // high green
          'rgba(255, 0, 0, 0)', // red
        ],
        'fill-opacity': 0.7,
      },
      layout: {
        visibility: 'visible',
      },
    });

    if ($('.slider-grid')[0]) {
      // remove existing slider, then only add new one
      if ($('.slider-grid')[0].noUiSlider) {
        $('.slider-grid')[0].noUiSlider.destroy();
      }

      const rotateSlider = noUiSlider.create($('.slider-grid')[0], {
        start: [this.gridRotateAngle],
        range: {
          min: [0],
          max: [90],
        },
        step: 2,
      });

      const sliderOnUpdate = (e) => {
        const val = rotateSlider.get();
        this.gridRotateAngle = parseFloat(val);
        this.gridSelectedIds = []; // reset selected ids as grid flow is reset, data is not valid anymore
        this.updateGridsData(drawID, {
          rotateAngle: this.gridRotateAngle,
          selectedIds: this.gridSelectedIds,
        });

        this.addGridToDraw(
          this.lastActiveDrawing[targetMap._mapId],
          targetMap,
          true
        );
      };

      rotateSlider.off('change', sliderOnUpdate);
      rotateSlider.on('change', sliderOnUpdate);

      rotateSlider.off('set', sliderOnUpdate);
      rotateSlider.on('set', sliderOnUpdate);

      this.rotateSlider = rotateSlider;
    }

    const getDrawingData = this.allDrawings.filter((drawing) => {
      return drawing.id == drawID;
    });

    let lastMouseMoveFeatureID = -1;

    const onMapMouseMoveForGrid = (event) => {
      const { point, originalEvent } = event;
      const isShiftKeyDown = originalEvent?.shiftKey;

      if (isShiftKeyDown) {
        const features = targetMap.queryRenderedFeatures(point);
        const featuresID = features[0].id;
        if (lastMouseMoveFeatureID == featuresID) {
          return;
        }
        if (!grids.features[featuresID]) {
          return;
        }
        if (!featuresID || !grids.features[featuresID].properties) {
          return;
        }
        const { selected } = grids.features[featuresID].properties;

        grids.features[featuresID].properties.selected = !selected;
        targetMap.getSource(sourceID).setData(grids);

        if (!this.gridSelectedIds.includes(featuresID)) {
          this.gridSelectedIds.push(featuresID);
        } else {
          this.gridSelectedIds.splice(
            this.gridSelectedIds.indexOf(featuresID),
            1
          );
        }

        this.updateGridsData(drawID, {
          rotateAngle: this.gridRotateAngle,
          selectedIds: this.gridSelectedIds,
        });

        lastMouseMoveFeatureID = featuresID;
      }
    };

    const onMapClickForGrid = ({ point }) => {
      const features = targetMap.queryRenderedFeatures(point);
      const featuresID = features[0].id;
      if (!grids.features[featuresID]) {
        return;
      }
      if (!featuresID || !grids.features[featuresID].properties) {
        return;
      }
      const { selected } = grids.features[featuresID].properties;

      grids.features[featuresID].properties.selected = !selected;
      targetMap.getSource(sourceID).setData(grids);

      if (!this.gridSelectedIds.includes(featuresID)) {
        this.gridSelectedIds.push(featuresID);
      } else {
        this.gridSelectedIds.splice(
          this.gridSelectedIds.indexOf(featuresID),
          1
        );
      }

      this.updateGridsData(drawID, {
        rotateAngle: this.gridRotateAngle,
        selectedIds: this.gridSelectedIds,
      });
    };

    this.onMapClickForGrid = onMapClickForGrid;
    this.onMapMouseMoveForGrid = onMapMouseMoveForGrid;

    targetMap.off('click', layerID, onMapClickForGrid);
    targetMap.off('mousemove', layerID, onMapMouseMoveForGrid);
    targetMap.on('click', layerID, onMapClickForGrid);
    targetMap.on('mousemove', layerID, onMapMouseMoveForGrid);

    if (!editMode) {
      targetMap.off('click', layerID, onMapClickForGrid);
      targetMap.off('mousemove', layerID, onMapMouseMoveForGrid);
      $('.slider-grid').attr('disabled', true);
      $('.rotateInput').attr('disabled', true);
      $('.reset-grid-button').attr('disabled', true);
    } else {
      $('.slider-grid').attr('disabled', false);
      $('.rotateInput').attr('disabled', false);
      $('.reset-grid-button').attr('disabled', false);
    }
  }

  calculateGridAreaSize(): number {
    let size = 0;
    this.allDrawings.forEach((drawing) => {
      const gridForDrawing = this.allGridsData.find(
        (grid) => grid.drawId == drawing.id
      );
      if (gridForDrawing) {
        if (gridForDrawing.selectedIds.length > 0) {
          size += gridForDrawing.selectedIds.length;
        } else {
          // if no grid box selected, get area size from plain drawing
          size += this.calculateAreaInMeterSq(drawing);
        }
      } else {
        // if no grid data found, add the drawing plain area size (w/o grid)
        size += this.calculateAreaInMeterSq(drawing);
      }
    });
    if (size !== 0) {
      $('.slider-grid').attr('disabled', false);
      $('.rotateInput').attr('disabled', false);
      $('.reset-grid-button').attr('disabled', false);
    }
    return size;
  }

  calculateAllDrawingsCentroid(): any {
    let points = [];
    if (this.allDrawings.length > 1) {
      this.allDrawings.forEach((drawing) => {
        const point = turf.centroid(drawing);
        points = [...points, [...point.geometry.coordinates]];
      });
      if (points.length > 0) {
        const shape = turf.lineString(points);
        const centroid = turf.centroid(shape);
        const coordinates = Array.from(centroid.geometry.coordinates);
        return coordinates.join(', ');
      } else {
        return null;
      }
    } else {
      const singleDrawCentroid = turf.centroid(this.allDrawings[0]);
      const singleCoordinates = Array.from(
        singleDrawCentroid.geometry.coordinates
      );
      return singleCoordinates.join(', ');
    }
  }

  calculateAreaInMeterSq(feature): number {
    return turf.area(feature);
  }

  onMapClickForGrid;
  onMapMouseMoveForGrid;

  updateGridsData(drawId, data = null): any {
    const initRotateAngle = this.map.getBearing(); // set init rotate angle to follow map bearing

    let gridData = {
      drawId: drawId,
      rotateAngle: initRotateAngle,
      selectedIds: [],
    };

    if (this.allGridsData.length > 0) {
      const existingGridData = this.allGridsData.find(
        (grid) => grid.drawId == drawId
      );
      if (existingGridData) {
        if (data) {
          existingGridData.rotateAngle = data.rotateAngle;
          existingGridData.selectedIds = data.selectedIds;
        } else {
          // update working data with loaded data
          this.gridRotateAngle = existingGridData.rotateAngle;
          this.gridSelectedIds = existingGridData.selectedIds;
        }

        gridData = existingGridData;
      } else {
        this.allGridsData.push(gridData);
        this.gridSelectedIds = [];
        this.gridRotateAngle = initRotateAngle;
      }
    } else {
      this.allGridsData.push(gridData);
      this.gridSelectedIds = [];
      this.gridRotateAngle = initRotateAngle;
    }

    const gridAreaSize = this.calculateGridAreaSize();
    this.annotationAreaSizeWithGrid$.next(gridAreaSize);

    // WIP : get centroid based on multiple drawings
    // const allDrawingCentroid = this.calculateAllDrawingsCentroid();
    // this.annotationCentroidWithGrid$.next(allDrawingCentroid);

    return gridData;
  }

  clickMarker(mark, el, url) {
    el.addEventListener('click', (evt) => {
      this.markerEvents$.next({
        onMarkerClick: { markerId: mark.properties.id, mouseEvent: evt },
      });
      this.map.flyTo({
        center: mark.geometry.coordinates,
        essential: true,
        duration: 2000,
      });
      this.router.navigate([
        `/projects/${el.getAttribute('marker_id')}/${url}`,
      ]);
    });
  }

  centerTo(coordinateArr) {
    this.map.flyTo({
      center: coordinateArr,
      essential: true,
      duration: 1000,
    });
  }

  setSearchCoordinate(coordinateArr = undefined) {
    if (coordinateArr === undefined) {
      this.markerSearchedCoordinate?.remove();
      return;
    }
    const el = document.createElement('div');
    el.className =
      'd-flex justify-content-center align-items-center flex-column';
    el.innerHTML = `<div class="search-label">${
      'Latitude: ' + coordinateArr[1] + ', Longitude: ' + coordinateArr[0]
    }</div><div class="point searched"></div>`;
    this.markerSearchedCoordinate = new mapboxgl.Marker(el)
      .setLngLat(coordinateArr)
      .addTo(this.map);

    this.map.flyTo({
      center: coordinateArr,
      essential: true,
      duration: 1000,
      zoom: 14,
    });
  }

  zoomIn(mark) {
    const btnZoom = document.getElementById(
      `mapbox-zoomin-${mark.properties.id}`
    );

    btnZoom.addEventListener('click', () => {
      this.map.flyTo({
        center: mark.geometry.coordinates,
        essential: true,
        duration: 1000,
      });

      this.map.once('moveend', () => {
        this.map.zoomTo(13, { duration: 1000 });
      });
    });
  }

  zoomToZoomLevel(value): void {
    this.map.once('moveend', () => {
      this.map.zoomTo(value, { duration: 1000 });
    });
  }

  showCoordinateLocation() {
    this.map.on('mousemove', function (e) {});
  }

  toggleDraw(mode) {
    // this.toggleAllAnnotation(false, undefined, undefined);
    const isDraw = this.isDraw$.getValue();
    this.isDraw$.next(mode);

    if (isDraw === null || isDraw !== mode) {
      this.changeDrawMode(mode);
    } else {
      this.isDraw$.next(null);
      this.changeDrawMode('simple_select');
    }

    // update area size on change tool or select new tool
    const newAreaSize = this.calculateGridAreaSize();
    this.annotationAreaSizeWithGrid$.next(newAreaSize);
  }

  changeDrawMode(mode, opt?): void {
    this.draw.changeMode(mode, opt);
  }

  addDraw(ctx, noFitBound?) {
    this.draw.add(ctx);
    // var bounds = new mapboxgl.LngLatBounds();
    // bounds.extend(ctx.geometry.coordinates[0]);
    if (!noFitBound) {
      let coordinates =
        ctx.geometry.type === 'LineString'
          ? ctx.geometry.coordinates
          : ctx.geometry.coordinates[0];

      if (this.lastActiveDrawId) {
        const lastDraw = this.allDrawings.filter(
          (drawing) => drawing.id == this.lastActiveDrawId
        );
        if (lastDraw.length != 0) {
          coordinates =
            lastDraw[0].geometry.type === 'LineString'
              ? lastDraw[0].geometry.coordinates
              : lastDraw[0].geometry.coordinates[0];
        }
      }

      this.fitboundCoordinates(coordinates, {
        padding: 20,
        offset: [-50, 0],
      });

      if (ctx.geometry.type === 'LineString')
        this.onLineStringCreated(ctx.geometry.coordinates);
    }
  }

  centerThenZoom(coordinate, zoomLevel = 9, callback?) {
    this.map.flyTo({
      center: coordinate,
      duration: 1000,
      essential: false,
    });
    this.map.once('moveend', () => {
      if (callback) callback();
      this.map.zoomTo(zoomLevel, { duration: 1000, essential: false });
    });
  }

  deleteSelectedAnnot(id?) {
    if (id) {
      this.changeDrawMode('simple_select', {
        featureIds: [id],
      });
    }

    const selected = this.draw.getSelected();
    const selectedIds = this.draw.getSelectedIds();
    const selectedPoints = this.draw.getSelectedPoints();

    if (
      selectedPoints.features.length > 0 &&
      selected.features[0].geometry.coordinates[0].length > 4
    ) {
      this.draw.trash();
      const newAreaSize = this.calculateGridAreaSize();
      this.annotationAreaSizeWithGrid$.next(newAreaSize);
    } else if (selectedIds.length > 0) {
      this.draw.delete(selectedIds);
      this.removeAllGrid();
      this.allDrawings = this.allDrawings.filter(
        (d) => d.id !== selectedIds[0]
      );
      this.allGridsData = this.allGridsData.filter(
        (grid) => grid.drawId !== selectedIds[0]
      );
      this.activeAnnotation$.next(null);
    }
    if (this.allDrawings.length === 0) this.hasDrawing$.next(false);
  }

  removeAllMarker() {
    return new Promise((resolve) => {
      const markers = this.mapMarkers;
      for (let marker of markers) {
        marker.remove();
      }
      resolve((this.mapMarkers = []));
    });
  }

  changeMapStyle(styleId) {
    this.map.setStyle(`mapbox://styles/${styleId}`);
  }

  // loadAirportDemo() {

  //   const proxy = 'https://proxy.aerodyne.dev/';
  //   const AIR_PORTS =
  //     proxy +
  //     'https://d2ad6b4ur7yvpq.cloudfront.net/naturalearth-3.3.0/ne_10m_airports.geojson';
  //   const layer1 = new GeoJsonLayer({
  //     id: 'airports',
  //     data: AIR_PORTS,
  //     filled: true,
  //     pointRadiusMinPixels: 2,
  //     opacity: 1,
  //     pointRadiusScale: 2000,
  //     getRadius: (f) => 11 - f.properties.scalerank,
  //     getFillColor: [200, 0, 80, 180],
  //     pickable: true,
  //     autoHighlight: true,
  //   });
  //   const layer2 = new ArcLayer({
  //     id: 'arcs',
  //     data: AIR_PORTS,
  //     dataTransform: (d) =>
  //       d.features.filter((f) => f.properties.scalerank < 4),
  //     // Styles
  //     getSourcePosition: (f) => [-0.4531566, 51.4709959], // London
  //     getTargetPosition: (f) => f.geometry.coordinates,
  //     getSourceColor: [0, 128, 200],
  //     getTargetColor: [200, 0, 80],
  //     getWidth: 1,
  //   });

  //   this.deck.setProps({ layers: [layer1, layer2] });
  // }

  loadSingle3DTilesDeckLayer(id: string, meta: any = null) {
    this.isThreeD$.next(true);
    this.deckIsActive = true;
    if (meta && meta !== null) {
      this.map.flyTo({
        ...this.INITIAL_VIEW_STATE,
        center: [meta.carto0, meta.carto1],
        // zoom: meta.zoom,
        speed: 5,
      });
    }

    const _3DLayer = new Tile3DLayer({
      id: 'tile-3d-layer',
      // tileset json file url
      data: 'https://assets.cesium.com/' + id + '/tileset.json',
      loader: CesiumIonLoader,
      pointSize: 1,
      // https://cesium.com/docs/rest-api/
      loadOptions: {
        'cesium-ion': {
          accessToken: environment.cesium.accessToken,
        },
      },
      onTilesetLoad: (tileset) => {
        // Recenter to cover the tileset
        const { cartographicCenter, zoom } = tileset;
        const nviewState = {
          ...this.INITIAL_VIEW_STATE,
          longitude: cartographicCenter[0],
          latitude: cartographicCenter[1],
          zoom,
        };
        this.deck.setProps({ initialViewState: nviewState });
      },
      // override scenegraph subLayer prop
      _subLayerProps: {
        scenegraph: { _lighting: 'flat' },
      },
    });

    this.deck.setProps({ layers: [_3DLayer] });
  }

  unloadAll3DTilesDeckLayer(id: string) {
    this.isThreeD$.next(false);
    const _3DLayerhide = new Tile3DLayer({
      id: 'tile-3d-layer',
      // tileset json file url
      data: 'https://assets.cesium.com/' + id + '/tileset.json',
      loader: CesiumIonLoader,
      pointSize: 1,
      opacity: 0,
      // https://cesium.com/docs/rest-api/
      loadOptions: {
        'cesium-ion': {
          accessToken:
            'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiIyMzE4MDQ5MS01ZGQ5LTQ4Y2YtYjM2NC0xN2M3ZjVkNDJhYzEiLCJpZCI6MzM3MTgsImlhdCI6MTYwMzI5MDEyNH0.-DXOwYMj_Uh_GEvB1tXNRShH7AmcK3rouQAEKZi6POQ',
        },
      },
      onTilesetLoad: (tileset) => {
        // Recenter to cover the tileset
        const { cartographicCenter, zoom } = tileset;
        const nviewState = {
          ...this.INITIAL_VIEW_STATE,
          longitude: cartographicCenter[0],
          latitude: cartographicCenter[1],
          zoom,
        };
        this.deck.setProps({ initialViewState: nviewState });
      },
      // override scenegraph subLayer prop
      _subLayerProps: {
        scenegraph: { _lighting: 'flat' },
      },
    });
    this.deckIsActive = false;
    this.deck.setProps({ layers: [_3DLayerhide] });
  }

  destroyAll3DTilesDeckLayer() {
    this.isThreeD$.next(false);
    this.isDeckLoaded$.subscribe((res) => {
      if (res) {
        this.deck.setProps({ layers: [] });
        this.deckIsActive = false;
      }
    });
  }

  get deckLayers(): any {
    return this.deck.layers;
  }

  toggleAnnotation(isShow, annotations: []) {}

  getMapScreenshot() {
    let blob = this.map.getCanvas().toDataURL();
    return blob;
  }

  getMapScreenshot3D(): any {
    html2canvas(document.querySelector('#potree_render_area')).then(
      (canvas) => {
        const blob = canvas.toDataURL('image/png');
        this.threeDScreenshotBlob$.next(blob);
      }
    );
  }

  mapOnClickFns: {
    [layerId: string]: (e) => any;
  } = {};

  mapOnMouseEnterFns: {
    [layerId: string]: (e) => any;
  } = {};

  mapOnMouseLeaveFns: {
    [layerId: string]: (e) => any;
  } = {};

  toggleAllAnnotation(isShow, map = this.map, annotations, skipZoom = false) {
    if (!this.isMapLoaded$.getValue()) return;

    const showMarker =
      this.locationStore.currLocation$.getValue().show_annotation_marker;

    if (isShow) {
      // this.lastActiveDrawId = "";
      this.splitViewService.lastActiveDrawIds = {};
      const severityType = this.genericTypeStore.getGenericType('severity');
      let fitBoundCoor = [];
      let features = [];
      let severities = [];
      let grids = [];
      this._clearAllAnnot(map);

      let _allDrawings = [];
      let _allGrids = [];
      let openlayerDrawing = [];

      let annotationMarkers = [];

      annotations.forEach((i, k) => {
        if (i.map_layer?.layer_type === 'facade') {
          let parsedArea = JSON.parse(i.area);
          let features = parsedArea.features.map((feature) => {
            let newFeature = {
              ...feature,
              properties: {
                severity_type: i.severity_type,
                annotation_id: i.id,
                location_id: i.location.id,
              },
            };
            return newFeature;
          });
          openlayerDrawing.push(JSON.stringify({ ...parsedArea, features }));
        } else {
          let data;
          try {
            data = JSON.parse(i.area);
          } catch (error) {
            console.log('problem annot', i);
          }
          if (!data?.drawing) return;
          data.drawing.forEach((drawing) => {
            features.push({
              ...drawing,
              properties: {
                ...drawing.properties,
                annotation_id: i.id,
                location_id: i?.location?.id || i.location,
                severity_id: i.severity_type.id,
                geometry: JSON.stringify(drawing.geometry),
                drawId: drawing.id,
                annotation_centroid: this.calculateCentroid(drawing),
              },
            });
            fitBoundCoor = [
              ...fitBoundCoor.concat(
                drawing.geometry.type === 'LineString'
                  ? drawing.geometry.coordinates
                  : drawing.geometry.coordinates[0]
              ),
            ];
          });

          // update drawing and grid data
          _allDrawings = [..._allDrawings, ...data.drawing];

          let gridData;
          if (data.grids) {
            gridData = Array.from(data.grids);
          } else {
            gridData = [];
          }

          _allGrids = [..._allGrids, ...gridData];

          severities.indexOf(i.severity_type.id) === -1 &&
            severities.push(i.severity_type.id);
          // bounds.extend(data.drawing[0].geometry.coordinates[0]);
        }
      });

      // draw grid for the first drawing
      // this.addGridToDraw(this.allDrawings[0], this.map, false); // disabled
      this.isMapLoaded$.subscribe((res) => {
        if (res) {
          if (!map.getSource('annotation-source')) {
            map.addSource('annotation-source', {
              type: 'geojson',
              data: {
                type: 'FeatureCollection',
                features: features.filter(
                  (i) => i.geometry.type !== 'LineString'
                ),
              },
            });
          }
          if (!map.getSource('annotation-source-line')) {
            map.addSource('annotation-source-line', {
              type: 'geojson',
              data: {
                type: 'FeatureCollection',
                features: features,
              },
            });
          }
          if (severityType) {
            for (const [key, val] of severities.entries()) {
              /**
               * annotation markers
               */
              if (showMarker) {
                let annotMarkerFeature = features.filter(
                  (i) => i.properties.severity_id === val
                );
                annotMarkerFeature = annotMarkerFeature.map((i) => {
                  return {
                    type: 'Feature',
                    geometry: {
                      type: 'Point',
                      coordinates: i.properties.annotation_centroid,
                    },
                    properties: {
                      ori_feature: JSON.stringify(i),
                      marker_color: severityType[val].description,
                    },
                  };
                });
                annotationMarkers = [
                  ...annotationMarkers,
                  ...annotMarkerFeature,
                ];
              }
              const mapLayerClick = (e) => {
                this._onClickLayer(e, map);
              };
              const mapLayerMouseEnter = () => {
                map.getCanvas().style.cursor = 'pointer';
              };
              const mapLayerMouseLeave = () => {
                map.getCanvas().style.cursor = '';
              };
              const annotLayerId = `annotation-layer-${val}`;

              if (!map.getSource(annotLayerId)) {
                map.addLayer({
                  id: annotLayerId,
                  type: 'fill',
                  source: 'annotation-source',
                  paint: {
                    'fill-color': severityType[val].description,
                    'fill-opacity': 0.4,
                    'fill-outline-color': severityType[val].description,
                  },
                  layout: {
                    visibility: 'visible',
                  },
                  filter: ['==', ['get', 'severity_id'], val],
                });

                map.on('click', annotLayerId, mapLayerClick);
                this.mapOnClickFns[annotLayerId] = mapLayerClick;

                map.on('mouseenter', annotLayerId, mapLayerMouseEnter);
                this.mapOnMouseEnterFns[annotLayerId] = mapLayerMouseEnter;

                map.on('mouseleave', annotLayerId, mapLayerMouseLeave);
                this.mapOnMouseLeaveFns[annotLayerId] = mapLayerMouseLeave;
              }

              const annotLayerStrokeId = `annotation-layer-stroke-${val}`;

              if (!map.getSource(annotLayerStrokeId)) {
                map.addLayer({
                  id: annotLayerStrokeId,
                  type: 'line',
                  source: 'annotation-source-line',
                  paint: {
                    'line-color': severityType[val].description,
                    'line-width': 2,
                  },
                  layout: {
                    visibility: 'visible',
                  },
                  filter: ['==', ['get', 'severity_id'], val],
                });
              }
            }

            if (showMarker) {
              if (map.getLayer('annotation-markers-layer'))
                map.removeLayer('annotation-markers-layer');
              if (map.getSource('annotation-markers-source'))
                map.removeSource('annotation-markers-source');
              map.addSource('annotation-markers-source', {
                type: 'geojson',
                data: {
                  type: 'FeatureCollection',
                  features: annotationMarkers,
                },
              });
              map.addLayer({
                id: 'annotation-markers-layer',
                type: 'circle',
                source: 'annotation-markers-source',
                paint: {
                  'circle-color': ['get', 'marker_color'],
                  'circle-radius': 6,
                  'circle-stroke-width': 2,
                  'circle-stroke-color': '#ffffff',
                },
              });
              map.on('mouseenter', 'annotation-markers-layer', (e) => {
                map.getCanvas().style.cursor = 'pointer';
              });
              map.on('click', 'annotation-markers-layer', (e) => {
                const ori_feature = JSON.parse(
                  e.features[0].properties.ori_feature
                );
                this._onClickLayer({ features: [ori_feature] }, map);
              });
              map.on('mouseleave', 'annotation-markers-layer', (e) => {
                map.getCanvas().style.cursor = '';
              });
            }
          }
          if (!skipZoom)
            this.fitboundCoordinates(fitBoundCoor, { padding: 100 });
        }
      });
      this.openlayerService.savedAnnot$.next({
        openlayerDrawings: openlayerDrawing,
      });
      this.openlayerService.allSavedAnnot$.next(openlayerDrawing);
    } else {
      if (this.annotationSubs) {
        this.annotationSubs.unsubscribe();
      }
      this._clearAllAnnot(map);
      if (this.annotationServiceFindSub)
        this.annotationServiceFindSub.unsubscribe();
    }
  }

  _onClickLayer(e, map = this.map) {
    const selectedMap =
      this.splitViewService.mapViews[this.splitViewService.selectedMapViewIndex]
        .map;
    if (selectedMap._mapId !== map._mapId) {
      this.toastr.error(
        this._t.instant('Invalid Action'),
        this._t.instant(`You can only select grid on active map`)
      );
      return;
    }

    const location_id = e.features[0].properties.location_id;
    const annotation_id = e.features[0].properties.annotation_id;
    const geometry = JSON.parse(e.features[0].properties.geometry);
    this._setImagesRelatedToCoor(geometry);

    if (geometry.type === 'LineString') {
      this.fitboundCoordinates(geometry.coordinates);
    } else {
      this.fitboundCoordinates(geometry.coordinates[0]);

      this.addGridToDraw(e.features[0], map, false);
    }

    this.router
      .navigate([`/projects/${location_id}/details/annotations`], {
        relativeTo: this.activatedRoute,
      })
      .then(() => {
        this.spinner.show();
        setTimeout(() => {
          const annotationCard = document.getElementById(
            `annotation-card-${annotation_id}`
          );
          if (annotationCard) {
            const annotationCardOffset = annotationCard.offsetTop;
            const annotationContainer = document.getElementById(
              'list-annotation-container'
            );
            const onScroll = function () {
              if (
                window.pageYOffset.toFixed() === annotationCardOffset.toFixed()
              ) {
                window.removeEventListener('scroll', onScroll);
                // this.spinner.hide();
              }
            };

            window.addEventListener('scroll', onScroll);
            onScroll();
            annotationContainer.scrollTo({
              top: annotationCardOffset,
              behavior: 'smooth',
            });
            this.spinner.hide();
          }
        }, 1500);
      });
  }

  toggleGalleryImages(isShow, map = this.map) {
    let clusterCoor = [];
    let clusterCoorIdx = [];
    let metadatas = [];
    const images = this.galleryImagesStore.galleryImages$
      .getValue()
      .filter((i) => i.tag === 'images');

    metadatas = images.reduce(
      (acc, i) => [
        ...acc,
        { location: i.location, ...JSON.parse(i.metadata_stringified) },
      ],
      []
    );
    metadatas.forEach((i, k) => {
      if (i.GpsLatitude && i.GpsLongitude) {
        const lat = isNaN(i.GpsLatitude)
          ? ConvertDMSToDD(i.GpsLatitude)
          : i.GpsLatitude;
        const lng = isNaN(i.GpsLongitude)
          ? ConvertDMSToDD(i.GpsLongitude)
          : i.GpsLongitude;
        clusterCoor.push([parseFloat(lat), parseFloat(lng)]);
        clusterCoorIdx.push(k);
      } else if (i.GpsLatitude && i.GpsLongtitude) {
        const lat = isNaN(i.GpsLatitude)
          ? ConvertDMSToDD(i.GpsLatitude)
          : i.GpsLatitude;
        const lng = isNaN(i.GpsLongtitude)
          ? ConvertDMSToDD(i.GpsLongtitude)
          : i.GpsLongtitude;
        clusterCoor.push([parseFloat(lat), parseFloat(lng)]);
        clusterCoorIdx.push(k);
      } else if (i.GPSLatitude && i.GPSLongitude) {
        const lat = isNaN(i.GPSLatitude)
          ? ConvertDMSToDD(i.GPSLatitude)
          : i.GPSLatitude;
        const lng = isNaN(i.GPSLongitude)
          ? ConvertDMSToDD(i.GPSLongitude)
          : i.GPSLongitude;
        clusterCoor.push([parseFloat(lat), parseFloat(lng)]);
        clusterCoorIdx.push(k);
      } else {
        console.error(i);
      }
    });
    const gc = new GeoCluster();
    if (!this.clusteredCoors) {
      this.clusteredCoors = gc.startCluster(clusterCoor, clusterCoorIdx);
    }

    if (isShow && metadatas[0]) {
      // set cluster layer
      const clusteredFeatures = this.clusteredCoors.map((i, key) => {
        return {
          type: 'Feature',
          properties: {
            centroid: i.centroid,
            items_count: i.elements.length,
          },
          geometry: {
            type: 'Point',
            coordinates: [i.centroid[1], i.centroid[0]],
          },
        };
      });
      if (!map.getSource('gallery-image-source')) {
        map.addSource('gallery-image-source', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: clusteredFeatures,
          },
        });
      }
      if (!map.getLayer('gallery-image-layer-overlay')) {
        map.addLayer({
          id: 'gallery-image-layer-overlay',
          type: 'background',
          paint: {
            'background-color': '#FFFFFF',
            'background-opacity': 0.4,
          },
        });
      }
      if (!map.getLayer('gallery-image-layer')) {
        map.addLayer({
          id: 'gallery-image-layer',
          type: 'symbol',
          source: 'gallery-image-source',
          layout: {
            'icon-image': 'gallery-image-marker',
            'icon-size': 0.5,
            'icon-allow-overlap': true,
            'text-field': ['get', 'items_count'],
            'text-allow-overlap': true,
          },
        });
      }

      this.makeClusterLayer(this.clusteredCoors, metadatas);

      map.on('click', 'gallery-image-layer', (e) => {
        this._clearSingleGalleryImagesMarker(map);

        const props = e.features[0].properties;
        const layerId = `gallery-image-layer-${props.centroid}`;
        for (let clusterCoor of this.clusteredCoors) {
          if (
            map.getLayer(
              `gallery-image-layer-${JSON.stringify(clusterCoor.centroid)}`
            )
          ) {
            map.setLayoutProperty(
              `gallery-image-layer-${JSON.stringify(clusterCoor.centroid)}`,
              'visibility',
              'none'
            );
          }
        }

        if (map.getLayer(layerId)) {
          const visibility = map.getLayoutProperty(layerId, 'visibility');
          map.setLayoutProperty(
            layerId,
            'visibility',
            visibility === 'visible' ? 'none' : 'visible'
          );
        }
      });
      map.on('mouseenter', 'gallery-image-layer', function () {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', 'gallery-image-layer', function () {
        map.getCanvas().style.cursor = '';
      });

      if (clusteredFeatures[0]) {
        const coordinates = clusteredFeatures.reduce(
          (acc, i) => [...acc, i.geometry.coordinates],
          []
        );
        this.fitboundCoordinates(coordinates, {
          padding: 100,
          offset: [-50, 0],
        });
      }
      this.isGalleryImages$.next(true);
    } else {
      this._clearSingleGalleryImagesMarker(map);
      this.clusteredCoors.forEach((clusterCoor, key) => {
        const layerId = `gallery-image-layer-${JSON.stringify(
          clusterCoor.centroid
        )}`;
        const sourceId = `gallery-image-source-${JSON.stringify(
          clusterCoor.centroid
        )}`;
        if (map?.getLayer(layerId)) map.removeLayer(layerId);
        if (map?.getSource(sourceId)) map.removeSource(sourceId);
      });
      if (map?.getLayer('gallery-image-layer-overlay'))
        map.removeLayer('gallery-image-layer-overlay');

      if (map?.getLayer('gallery-image-layer'))
        map.removeLayer('gallery-image-layer');
      if (map?.getSource('gallery-image-source'))
        map.removeSource('gallery-image-source');

      this.clusteredCoors = null;
      this.isGalleryImages$.next(false);
    }
  }

  makeClusterLayer(clusteredCoors, metadatas, map = this.map) {
    clusteredCoors.forEach((clusterCoor, key) => {
      const sourceId = `gallery-image-source-${JSON.stringify(
        clusterCoor.centroid
      )}`;
      const layerId = `gallery-image-layer-${JSON.stringify(
        clusterCoor.centroid
      )}`;
      if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
      const clusterChildrenFeatures = clusterCoor.elements.map((i, k) => {
        return {
          type: 'Feature',
          properties: {
            gallery_index: clusterCoor.elementsImgKey[k],
            location_id: this.locationStore.currLocation$.getValue().id,
            coordinate: [i[1], i[0]],
            rotation: parseFloat(
              metadatas[clusterCoor.elementsImgKey[k]].GimbalYawDegree
            ),
            metadata: JSON.stringify(metadatas[clusterCoor.elementsImgKey[k]]),
          },
          geometry: {
            type: 'Point',
            coordinates: [i[1], i[0]],
          },
        };
      });
      map.addSource(sourceId, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: clusterChildrenFeatures,
        },
      });
      map.addLayer(
        {
          id: layerId,
          type: 'symbol',
          source: sourceId,
          layout: {
            visibility: 'none',
            'icon-image': 'gallery-image-marker-arrow',
            'icon-size': 0.7,
            'icon-rotate': ['get', 'rotation'],
            'icon-rotation-alignment': 'map',
            'icon-allow-overlap': true,
          },
        },
        'gallery-image-layer'
      );
      map.on('click', layerId, (e) => {
        this._clearSingleGalleryImagesMarker(map);
        const props = e.features[0].properties;
        //show custom html marker
        this._markerDataGalleryImage(props);
        if (
          this.router.url !== `/projects/${props.location_id}/details/gallery`
        ) {
          this.router
            .navigateByUrl(`/projects/${props.location_id}/details/gallery`)
            .then(() => {
              setTimeout(() => {
                this.galleryImagesStore.setCurrentGalleryImages = parseInt(
                  props.gallery_index
                );
              });
            });
        } else {
          this.galleryImagesStore.setCurrentGalleryImages = parseInt(
            props.gallery_index
          );
        }
      });
      map.on('mouseenter', layerId, function () {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', layerId, function () {
        map.getCanvas().style.cursor = '';
      });
    });
  }

  showSingleGalleryImagesMarker(gallery_index, map = this.map) {
    this._clearSingleGalleryImagesMarker(map);

    //check if gallery image layer & source exist
    if (
      this.map.getLayer('gallery-image-layer') &&
      this.map.getSource('gallery-image-source')
    ) {
      let metadata;
      const images = this.galleryImagesStore.galleryImages$.getValue();

      if (images[0]) {
        const img = images[gallery_index];
        metadata = {
          location: img.location,
          ...JSON.parse(img.metadata_stringified),
        };
        map.addSource('gallery-image-source-single', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                properties: {
                  gallery_index: gallery_index,
                  location_id: metadata.location,
                  coordinate: [metadata.GpsLongitude, metadata.GpsLatitude],
                  rotation: parseFloat(metadata.GimbalYawDegree),
                },
                geometry: {
                  type: 'Point',
                  coordinates: [metadata.GpsLongitude, metadata.GpsLatitude],
                },
              },
            ],
          },
        });
        map.addLayer({
          id: 'gallery-image-layer-single',
          type: 'symbol',
          source: 'gallery-image-source-single',
          layout: {
            'icon-image': 'gallery-image-marker-arrow',
            'icon-size': 0.7,
            'icon-rotate': ['get', 'rotation'],
            'icon-allow-overlap': true,
          },
        });
        map.on('click', 'gallery-image-layer-single', (e) => {
          this._markerDataGalleryImage({
            metadata: img.metadata_stringified,
            coordinate: JSON.stringify([
              parseFloat(metadata.GpsLongitude),
              parseFloat(metadata.GpsLatitude),
            ]),
          });
          this.galleryImagesStore.setCurrentGalleryImages = gallery_index;
        });
      }
    }
  }

  _markerDataGalleryImage(props, map = this.map) {
    const metadata = JSON.parse(props.metadata);
    const el = document.createElement('div');
    if (this.markerDataGalleryImage) this.markerDataGalleryImage.remove();
    // if (this.prevMarkerDataGalleryImage === props.coordinate) {
    el.innerHTML = `<div class="marker-data-gallery-img">
        <div class="top">${metadata.GimbalYawDegree}°</div>
        <div class="middle"> <div class="left">${metadata.AbsoluteAltitude}m</div> <div class="right">${metadata.GimbalPitchDegree}°</div> </div>
        <!--<div class="bottom">test</div>--> </div>
        <div id="markerDataGalleryImgDetails" class="marker-data-gallery-img-details">
        <div>
          <p class="title text-left">Date & Time</p>
          <p class="description">${metadata.CreateDate}</p>
          <p class="title text-left">Latitude</p>
          <p class="description">${metadata.GpsLatitude}</p>
          <p class="title text-left">Longitude</p>
          <p class="description">${metadata.GpsLongitude}</p>
        </div>
        <div class="middle">
          <p class="title text-left">Pitch</p>
          <p class="description">${metadata.GimbalPitchDegree}</p>
          <p class="title text-left">Roll</p>
          <p class="description">${metadata.GimbalRollDegree}</p>
          <p class="title text-left">Yaw</p>
          <p class="description">${metadata.GimbalYawDegree}</p>
        </div>
        <div>
          <p class="title text-left">Focal Length</p>
          <p class="description">${metadata.FocalLength}</p>
          <p class="title text-left">ISO Speed Ratings</p>
          <p class="description">${metadata.ISO}</p>
          <p class="title text-left">Width x Height</p>
          <p class="description">${metadata.ImageSize}</p>
        </div>
      </div>`;
    // } else {
    //   this.prevMarkerDataGalleryImage = props.coordinate
    //   el.innerHTML = `<div class="marker-data-gallery-img">
    //     <div class="top">${metadata.GimbalYawDegree}°</div>
    //     <div class="middle"> <div class="left">${metadata.AbsoluteAltitude}m</div> <div class="right">${metadata.GimbalPitchDegree}°</div> </div>
    //     <!--<div class="bottom">test</div>--> </div>`;
    // }

    this.markerDataGalleryImage = new mapboxgl.Marker(el, {
      rotationAlignment: 'auto',
    })
      .setLngLat(JSON.parse(props.coordinate))
      .addTo(map);
  }

  _clearSingleGalleryImagesMarker(map = this.map) {
    if (this.markerDataGalleryImage) this.markerDataGalleryImage.remove();
    if (map?.getLayer('gallery-image-layer-single'))
      map.removeLayer('gallery-image-layer-single');
    if (map?.getSource('gallery-image-source-single'))
      map.removeSource('gallery-image-source-single');
  }

  _clearAllAnnot(map = this.map) {
    this.openlayerService.savedAnnot$.next({
      openlayerDrawings: undefined,
    });
    this.allDrawings = [];
    this.allGridsData = [];
    this.gridRotateAngle = 0;
    this.gridSelectedIds = [];
    const severityType = this.genericTypeStore.getGenericType(
      'severity',
      null,
      null,
      true
    );
    if (severityType?.entries())
      for (const [key, val] of severityType?.entries()) {
        const annotLayerId = `annotation-layer-${val.id}`;
        if (map.getLayer(annotLayerId)) {
          map.removeLayer(annotLayerId);
          map.off('click', annotLayerId, this.mapOnClickFns[annotLayerId]);
          map.off(
            'mouseenter',
            annotLayerId,
            this.mapOnMouseEnterFns[annotLayerId]
          );
          map.off(
            'mouseleave',
            annotLayerId,
            this.mapOnMouseLeaveFns[annotLayerId]
          );
        }

        const annotLayerStrokeId = `annotation-layer-stroke-${val.id}`;
        if (map.getLayer(annotLayerStrokeId)) {
          map.removeLayer(annotLayerStrokeId);
        }
      }
    if (map.getSource('annotation-source'))
      map.removeSource('annotation-source');
    if (map.getSource('annotation-source-line'))
      map.removeSource('annotation-source-line');
    if (map?.getLayer('annotation-markers-layer'))
      map.removeLayer('annotation-markers-layer');
    if (map?.getSource('annotation-markers-source'))
      map.removeSource('annotation-markers-source');
  }

  toggleGrid(isOn?) {
    this.isGrid$.next(isOn);
    const map =
      this.splitViewService.mapViews[this.splitViewService.selectedMapViewIndex]
        .map;
    if (isOn) {
      // for (const map of allMaps) {
      const editable: boolean =
        this.router.url.indexOf('edit') >= 0 ||
        this.router.url.indexOf('create') >= 0;

      this.addGridToDraw(null, map, editable, true);
      // }
    } else {
      // for (const map of allMaps) {
      this.removeAllGrid(map);
      // }
      $('.slider-grid').attr('disabled', true);
      $('.rotateInput').attr('disabled', true);
      $('.reset-grid-button').attr('disabled', true);
    }
  }

  fitboundCoordinates(coordinates, offset?) {
    if (coordinates.length === 0) {
      return null;
    }
    if (!offset) {
      offset = {
        padding: 20,
        offset: [-50, 0],
        pitch: 60,
        duration: 2000,
        essential: true,
      };
    } else {
      offset['duration'] = 2000;
      offset['essential'] = true;
    }
    try {
      const bounds = coordinates.reduce(function (bounds, coord) {
        return bounds.extend(coord);
      }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));
      this.splitViewService.onEvent$.next({
        onFitBounds: true,
        bounds: bounds,
        offset: offset,
      });
    } catch (error) {}
  }

  // findImagesRelatedToCoorMultiPoints is endpoint to process multiple points
  _setImagesRelatedToCoor(geometry) {
    const centroid = this.calculateCentroid(geometry);
    if (
      JSON.stringify(this.prevCentroid) !== JSON.stringify(centroid) ||
      this.dataService.featureImages$.getValue().length === 0
    ) {
      this.annotationServiceFindSub = this.annotationService
        .findImagesRelatedToCoorMultiPoints(
          this.locationStore.currLocation$.getValue().name,
          geometry
        )
        .subscribe(
          (res) => {
            this.dataService.featureImages$.next(res[0].slice(0, 20));
          },
          (error) => {}
        );
      this.prevCentroid = centroid;
    }
  }

  mouseIsDown = false;

  // raycaster = new THREE.Raycaster();
  // mouseThis = new THREE.Vector2();
  // tempV = new THREE.Vector3();

  raycaster: any;
  // mouseThis = new THREE.Vector2();
  tempV: any;

  allMesh = [];
  referenceCards: any[] = [];

  removeSpotlight3D() {
    this.referenceCards.length = 0;
    this.allMesh.length = 0;
    if (this.map.getLayer('three-layer')) this.map.removeLayer('three-layer');
    $('.three-anchors').remove();
  }

  loadSpotlight3D(imagePaths, center?: []) {
    //return;
    this.removeSpotlight3D();

    if (!imagePaths) return;

    let threeCenter = center
      ? center
      : [this.map.getCenter().lng, this.map.getCenter().lat];
    // parameters to ensure the model is georeferenced correctly on the map
    var modelOrigin = threeCenter;
    var modelAltitude = 0;
    var modelRotate = [Math.PI / 2, 0, 0];

    var modelAsMercatorCoordinate = mapboxgl.MercatorCoordinate.fromLngLat(
      modelOrigin,
      modelAltitude
    );

    // transformation parameters to position, rotate and scale the 3D model onto the map
    var modelTransform = {
      translateX: modelAsMercatorCoordinate.x,
      translateY: modelAsMercatorCoordinate.y,
      translateZ: modelAsMercatorCoordinate.z,
      rotateX: modelRotate[0],
      rotateY: modelRotate[1],
      rotateZ: modelRotate[2],
      scale: modelAsMercatorCoordinate.meterInMercatorCoordinateUnits() * 10,
    };

    //var THREE = window.THREE;

    const env = this;
    env.raycaster = new THREE.Raycaster();
    env.tempV = new THREE.Vector3();

    // configuration of the custom layer for a 3D model per the CustomLayerInterface
    var customLayer = {
      id: 'three-layer',
      type: 'custom',
      renderingMode: '3d',
      onAdd: function (map, gl) {
        const width = window.innerWidth;
        const height = window.innerHeight;
        this.camera = new THREE.PerspectiveCamera(60, width / height, 1, 2100);
        this.scene = new THREE.Scene();

        // create two three.js lights to illuminate the model
        // var directionalLight = new THREE.DirectionalLight(0xffffff);
        // directionalLight.position.set(0, -70, 100).normalize();
        // this.scene.add(directionalLight);

        // var directionalLight2 = new THREE.DirectionalLight(0xffffff);
        // directionalLight2.position.set(0, 70, 100).normalize();
        // this.scene.add(directionalLight2);
        this.map = map;

        const geometry = new THREE.PlaneGeometry(1, 1, 1);
        const materialColor = new THREE.MeshBasicMaterial({
          color: 0x333333,
          side: THREE.DoubleSide,
        });

        const count = 5;
        let imageMesh;
        const labelContainerElem = document.querySelector('#labels');
        env.referenceCards.length = 0;
        const proxy = 'https://proxy.aerodyne.dev/';

        for (let i = 0; i < count; i++) {
          const card = {
            bgUrl: imagePaths[i].split(proxy).join(''),
          };

          env.referenceCards.push(card);

          imageMesh = new THREE.Mesh(geometry, materialColor);
          imageMesh.visible = false;

          const t = (i / count) * 2 * Math.PI;

          imageMesh.position.x = Math.cos(t) * 2;
          imageMesh.position.z = Math.sin(t) * 2;
          imageMesh.position.y = 0.5;
          imageMesh.userData = { id: i };
          this.scene.add(imageMesh);

          env.allMesh.push(imageMesh);

          const elem = document.createElement('div');
          $(elem).css('width', '10px');
          $(elem).css('height', '10px');
          $(elem).css('backgroundColor', 'red');
          elem.textContent = `#${i}`;
          elem.setAttribute('id', 'elem-' + i);
          elem.setAttribute('class', 'three-anchors');
          labelContainerElem.appendChild(elem);
        }

        // use the Mapbox GL JS map canvas for three.js
        this.renderer = new THREE.WebGLRenderer({
          canvas: map.getCanvas(),
          context: gl,
          antialias: true,
        });

        this.renderer.autoClear = false;
        this.renderer.render(this.scene, this.camera);
      },
      render: function (gl, matrix) {
        var rotationX = new THREE.Matrix4().makeRotationAxis(
          new THREE.Vector3(1, 0, 0),
          modelTransform.rotateX
        );
        var rotationY = new THREE.Matrix4().makeRotationAxis(
          new THREE.Vector3(0, 1, 0),
          modelTransform.rotateY
        );
        var rotationZ = new THREE.Matrix4().makeRotationAxis(
          new THREE.Vector3(0, 0, 1),
          modelTransform.rotateZ
        );

        var m = new THREE.Matrix4().fromArray(matrix);
        var l = new THREE.Matrix4()
          .makeTranslation(
            modelTransform.translateX,
            modelTransform.translateY,
            modelTransform.translateZ
          )
          .scale(
            new THREE.Vector3(
              modelTransform.scale,
              -modelTransform.scale,
              modelTransform.scale
            )
          )
          .multiply(rotationX)
          .multiply(rotationY)
          .multiply(rotationZ);

        env.allMesh.forEach((mesh, index) => {
          // get the position of the center of the cube
          mesh.updateWorldMatrix(true, false);
          mesh.getWorldPosition(env.tempV);

          const elem = $('#elem-' + index)[0];

          // get the normalized screen coordinate of that position
          // x and y will be in the -1 to +1 range with x = -1 being
          // on the left and y = -1 being on the bottom
          env.tempV.project(this.camera);

          // ask the raycaster for all the objects that intersect
          // from the eye toward this object's position
          env.raycaster.setFromCamera(env.tempV, this.camera);
          const intersectedObjects = env.raycaster.intersectObjects(
            this.scene.children
          );
          // We're visible if the first intersection is this object.
          const show =
            intersectedObjects.length && mesh === intersectedObjects[0].object;

          // convert the normalized position to CSS coordinates
          const x = (env.tempV.x * 0.5 + 0.5) * gl.canvas.clientWidth;
          const y = (env.tempV.y * -0.5 + 0.5) * gl.canvas.clientHeight;
          const z = (env.tempV.z * -0.5 + 0.5) * this.map.getZoom();

          const zoom = z * 6;

          // move the elem to that position
          elem.style.transform = `translate(-50%, -50%) translate(${x}px,${y}px) scale(${zoom})`;

          // set the zIndex for sorting
          elem.style.zIndex = ((-env.tempV.z * 0.5 + 0.5) * 100000) | 0;

          let cardZoom = zoom;
          if (cardZoom > 1.3) {
            cardZoom = 1.3;
          }

          const cardElem = $('#referenced-card-' + index)[0];
          cardElem.style.transform = `translate(-50%, -50%) translate(${x}px,${y}px) scale(${cardZoom})`;
          cardElem.style.zIndex = ((-env.tempV.z * 0.5 + 0.5) * 100000) | 0;
        });

        this.camera.projectionMatrix = m.multiply(l);
        this.renderer.state.reset();
        this.renderer.render(this.scene, this.camera);
        this.map.triggerRepaint();
      },
    };

    env.map.addLayer(customLayer);
  }

  loadGeoJsons(geojson, map = this.map, geojsonId) {
    const features = geojson.default?.features || geojson.features;

    this.removeGeoJsons(map, geojsonId);

    let popup = new mapboxgl.Popup({ closeButton: false });

    /**
     * example extra props:
     * FILL_COLOR: '#1B9BBB',
     * OPACITY: 1,
     * OUTLINE_COLOR: '#1B9BBB',
     * LINE_COLOR: '#1B9BBB',
     * LINE_WIDTH: 10,
     */
    map.addSource(`geojson-source-${geojsonId}`, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: features.filter((i) =>
          i.geometry?.type.toLowerCase().includes('polygon')
        ),
      },
    });
    map.addSource(`geojson-source-line-${geojsonId}`, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: features.filter((i) =>
          i.geometry?.type.toLowerCase().includes('line')
        ),
      },
    });
    map.addSource(`geojson-source-point-${geojsonId}`, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: features.filter((i) =>
          i.geometry?.type.toLowerCase().includes('point')
        ),
      },
    });

    map.addLayer({
      id: `geojson-layer-${geojsonId}`,
      type: 'fill',
      source: `geojson-source-${geojsonId}`,
      paint: {
        'fill-color': [
          'case',
          ['==', ['has', '_f_color'], true],
          ['get', '_f_color'],
          '#FFFF00',
        ],
        'fill-opacity': [
          'case',
          ['==', ['has', '_f_opacity'], true],
          ['get', '_f_opacity'],
          0.8,
        ],
        'fill-outline-color': [
          'case',
          ['==', ['has', '_f_o_color'], true],
          ['get', '_f_o_color'],
          '#FFFF00',
        ],
      },
      layout: {
        visibility: 'visible',
      },
    });

    map.addLayer({
      id: `geojson-layer-line-${geojsonId}`,
      type: 'line',
      source: `geojson-source-line-${geojsonId}`,
      paint: {
        'line-color': [
          'case',
          ['==', ['has', '_l_color'], true],
          ['get', '_l_color'],
          '#FFFF00',
        ],
        'line-width': [
          'case',
          ['==', ['has', '_l_width'], true],
          ['get', '_l_width'],
          4,
        ],
        /**
         * line-dasharray only supported >= 2.3.0
         * current version is: 1.9.1
         * */
        // 'line-dasharray': [
        //   'case',
        //   ['==', ['has', '_l_darray'], true],
        //   ['get', '_l_darray'],
        //   [1],
        // ],
      },
      layout: {
        visibility: 'visible',
      },
    });

    map.addLayer({
      id: `geojson-layer-point-${geojsonId}`,
      type: 'circle',
      source: `geojson-source-point-${geojsonId}`,
      paint: {
        'circle-color': [
          'case',
          ['==', ['has', '_c_color'], true],
          ['get', '_c_color'],
          '#FFFF00',
        ],
        'circle-radius': [
          'case',
          ['==', ['has', '_c_radius'], true],
          ['get', '_c_radius'],
          6,
        ],
        'circle-stroke-width': [
          'case',
          ['==', ['has', '_c_s_width'], true],
          ['get', '_c_s_width'],
          2,
        ],
        'circle-stroke-color': [
          'case',
          ['==', ['has', '_c_s_color'], true],
          ['get', '_c_s_color'],
          '#ffffff',
        ],
      },
      layout: {
        visibility: 'visible',
      },
    });

    // When a click event occurs on a feature in the places layer, open a popup at the
    // location of the feature, with description HTML from its properties.
    map.on('mouseenter', `geojson-layer-${geojsonId}`, function (e) {
      map.getCanvas().style.cursor = 'pointer';
      // let description = `${e.features[0].properties.Area?.toFixed(2) || 0} m²`;
      // if (!e.features[0].properties.Area) description = 'Unavailable'
      let description = '';
      description = Object.entries(e.features[0].properties).reduce(
        (acc, o) => {
          if (o[1] === null || o[1] === 'null' || o[0].startsWith('_')) {
            return acc;
          }
          return acc + `<strong>${o[0]}</strong>: ${o[1]}<br/>`;
        },
        ''
      );
      if (description.length === 0) description = 'Unavailable';

      popup = new mapboxgl.Popup({ closeButton: false });

      popup.setLngLat(e.lngLat).setHTML(description).addTo(map);

      // console.log(popup)
    });

    map.on('mouseleave', `geojson-layer-${geojsonId}`, function () {
      map.getCanvas().style.cursor = '';

      if (popup) popup.remove();
    });

    map.on('mouseenter', `geojson-layer-line-${geojsonId}`, function (e) {
      map.getCanvas().style.cursor = 'pointer';
      let description = '';
      description = Object.entries(e.features[0].properties).reduce(
        (acc, o) => {
          if (o[1] === null || o[1] === 'null' || o[0].startsWith('_')) {
            return acc;
          }
          return acc + `<strong>${o[0]}</strong>: ${o[1]}<br/>`;
        },
        ''
      );
      if (description.length === 0) description = 'Unavailable';
      popup = new mapboxgl.Popup({ closeButton: false });
      popup.setLngLat(e.lngLat).setHTML(description).addTo(map);
    });
    map.on('mouseleave', `geojson-layer-line-${geojsonId}`, function () {
      map.getCanvas().style.cursor = '';
      if (popup) popup.remove();
    });

    // map.on('mouseenter', `geojson-layer-point-${geojsonId}`, function (e) {
    //   map.getCanvas().style.cursor = 'pointer';
    //   let description = '';
    //   description = Object.entries(e.features[0].properties).reduce(
    //     (acc, o) => {
    //       if (o[1] === null || o[1] === 'null' || o[0].startsWith('_')) {
    //         return acc;
    //       }
    //       return acc + `<strong>${o[0]}</strong>: ${o[1]}<br/>`;
    //     },
    //     ''
    //   );
    //   if (description.length === 0) description = 'Unavailable';
    //   popup = new mapboxgl.Popup({ closeButton: false });
    //   popup.setLngLat(e.lngLat).setHTML(description).addTo(map);
    // });
    // map.on('mouseleave', `geojson-layer-point-${geojsonId}`, function () {
    //   map.getCanvas().style.cursor = '';
    //   if (popup) popup.remove();
    // });

    let that = this;
    map.on('click', `geojson-layer-point-${geojsonId}`, function (e) {
      var geojsonPointElem = document.getElementById(
        `geojson-layer-point-${geojsonId}`
      );
      if (geojsonPointElem === null) {
        let lngLat = Object.entries(e.lngLat);
        let coor = [lngLat[0][1], lngLat[1][1]];
        that.centerTo(coor);
        let description = `<div id="geojson-layer-point-${geojsonId}">`;
        description = Object.entries(e.features[0].properties).reduce(
          (acc, o) => {
            if (o[1] === null || o[1] === 'null' || o[0].startsWith('_')) {
              return acc;
            }
            if (o[0] === 'Report') {
              return (
                acc +
                `<strong>${o[0]}</strong>: <a href="${o[1]}" target="_blank">View Report</a> <br/>`
              );
            }
            return acc + `<strong>${o[0]}</strong>: ${o[1]}<br/>`;
          },
          ''
        );
        if (description.length === 0) description = 'Unavailable';
        popup = new mapboxgl.Popup({ closeButton: false });
        popup
          .setLngLat(e.lngLat)
          .setHTML(description + '</div>')
          .addTo(map);
      } else {
        if (popup) popup.remove();
      }
    });

    map.on('closeAllPopups', () => {
      popup.remove();
    });

    map.on(`closePopups-${geojsonId}`, () => {
      popup.remove();
    });
  }

  removeOrthoMany(map = this.map, geojsonId) {
    console.log('remove ortho');
  }

  removeGeoJsons(map = this.map, geojsonId) {
    if (!map) return;

    if (geojsonId && geojsonId !== 'all') {
      map.fire(`closePopups-${geojsonId}`);
      if (map.getSource(`geojson-source-${geojsonId}`)) {
        map.removeLayer(`geojson-layer-${geojsonId}`);
        map.removeSource(`geojson-source-${geojsonId}`);
      }
      if (map.getSource(`geojson-source-line-${geojsonId}`)) {
        map.removeLayer(`geojson-layer-line-${geojsonId}`);
        map.removeSource(`geojson-source-line-${geojsonId}`);
      }
      if (map.getSource(`geojson-source-point-${geojsonId}`)) {
        map.removeLayer(`geojson-layer-point-${geojsonId}`);
        map.removeSource(`geojson-source-point-${geojsonId}`);
      }
    } else {
      map.fire('closeAllPopups');
      const gjson = map
        .getStyle()
        .layers.filter((l) => l.id.includes('geojson-source'));

      // console.log(gjson);

      gjson.forEach((l) => {
        map.removeLayer(l.id);
        map.removeSource(l.source);
      });
    }
  }

  autoRotateMap() {
    let bearingNext = this.map.getBearing();
    bearingNext -= 0.2;
    this.map.setBearing(bearingNext);
  }

  rafId = 0;
  rotateNeedsPause: boolean = true;

  onTick() {
    if (this.isSpotlightMode$.getValue() === true && !this.rotateNeedsPause) {
      this.autoRotateMap();
    }
    this.rafId = requestAnimationFrame(this.onTick.bind(this));
  }

  handleRequestGridReset() {
    // calculation grid from 0 - 90˚

    const bearingValue = this.map.getBearing();
    let calcRotationAngle: number = 0;
    const lastActiveDrawId =
      this.splitViewService.lastActiveDrawIds[
        this.splitViewService.selectedMap._mapId
      ];

    if (bearingValue < 0) {
      // if bearing -ve value
      if (bearingValue < -90) {
        calcRotationAngle = 180 + bearingValue;
      } else {
        calcRotationAngle = 90 + bearingValue;
      }
    } else {
      if (bearingValue <= 90) {
        calcRotationAngle = bearingValue;
      } else {
        calcRotationAngle = bearingValue - 90;
      }
    }

    calcRotationAngle = Number(
      Math.round(Number(calcRotationAngle + 'e3')) + 'e-3'
    );

    this.updateGridsData(lastActiveDrawId, {
      rotateAngle: calcRotationAngle,
      selectedIds: [],
    }); // force rotationAngle to 0
    const draw = this.allDrawings.find((draw) => draw.id == lastActiveDrawId);
    const editable =
      this.router.url.indexOf('create') >= 0 ||
      this.router.url.indexOf('edit') >= 0;

    this.addGridToDraw(draw, this.map, editable);
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.rafId);
  }
}

interface IMarkerEvents {
  onMarkerEnter?: { markerTitle?: string; markerId: string; mouseEvent: any };
  onMarkerLeave?: { markerId: string; mouseEvent: any };
  onMarkerClick?: { markerId: string; mouseEvent: any };
}
